import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { fetchData } from '../../../../helpers/methods';

import Modal from '../../../../component-library/modal';
import Tabs from '../../../../component-library/tabs';
import Button from '../../../../component-library/button';
import ButtonIcon from '../../../../component-library/button-icon';
import FormField from '../../../../component-library/form-field';
import Pagination from '../../../../component-library/pagination';

import DesignSearch from './design-search';

const RefDataSearchModal = ({
	type = 'default',
	handleCloseModal,
	handleApplyData,
}) => {
	const [activeTab, setActiveTab] = useState(0);
	const [appsData, setAppsData] = useState([]);
	const [prfsData, setPrfsData] = useState([]);
	const [selectedData, setSelectedData] = useState({});
	const [showDesignSearch, setShowDesignSearch] = useState(false);
	const [appsPagination, setAppsPagination] = useState({
		totalPages: 1,
		currentPage: 1,
	});
	const [prfsPagination, setPrfsPagination] = useState({
		totalPages: 1,
		currentPage: 1,
	});
	const [filter, setFilter] = useState({});
	const [tabData, setTabData] = useState([
		{
			index: 0,
			id: 'apps',
			label: 'Internal',
			notifications: 0,
		},
		{
			index: 1,
			id: 'prfs',
			label: 'External',
			notifications: 0,
		},
	]);
	const pageSize = 10;

	const getAppsData = (currentPage, clearSearch) => {
		let filterString = '';
		if (clearSearch !== true) {
			if (filter && Object.keys(filter).length !== 0) {
				Object.entries(filter).forEach(entry => {
					const [key, value] = entry;
					if (value.length > 0) {
						filterString = filterString.concat(`&${key}=${value.toString()}`);
					}
				});
			}
		}

		const newCurrentPage = currentPage - 1;

		fetchData({
			uri: `/api/web/refdatas/apps/search?page=${newCurrentPage}&pageSize=${pageSize}${filterString}`,
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					const metaData = body.metadata;
					const refData = body.results;
					setAppsPagination({
						totalPages: Math.ceil(metaData.totalSize / pageSize),
						currentPage: metaData.page + 1,
					});
					setAppsData(refData);
					const newTabData = [...tabData];
					newTabData[0].notifications = metaData.totalSize;
					setTabData(newTabData);
				} else {
					setAppsData([]);
				}
			})
			.catch(() => {
				setAppsData([]);
			});
	};

	const getPrfsData = (currentPage, clearSearch) => {
		let filterString = '';
		if (clearSearch !== true) {
			if (filter && Object.keys(filter).length !== 0) {
				Object.entries(filter).forEach(entry => {
					const [key, value] = entry;
					if (value.length > 0) {
						filterString = filterString.concat(`&${key}=${value.toString()}`);
					}
				});
			}
		}

		const newCurrentPage = currentPage - 1;

		fetchData({
			uri: `/api/web/refdatas/prfs/search?page=${newCurrentPage}&pageSize=${pageSize}${filterString}`,
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					const metaData = body.metadata;
					const refData = body.results;
					setPrfsPagination({
						totalPages: Math.ceil(metaData.totalSize / pageSize),
						currentPage: metaData.page + 1,
					});
					setPrfsData(refData);
					const newTabData = [...tabData];
					newTabData[1].notifications = metaData.totalSize;
					setTabData(newTabData);
				} else {
					setPrfsData([]);
				}
			})
			.catch(() => {
				setPrfsData([]);
			});
	};

	const handleAppsPagination = currentPage => {
		setAppsPagination({
			...appsPagination,
			currentPage,
		});
		getAppsData(currentPage, false);
	};

	const handlePrfsPagination = currentPage => {
		setPrfsPagination({
			...prfsPagination,
			currentPage,
		});
		getPrfsData(currentPage, false);
	};

	const handleFilterChange = (key, value) => {
		const newFilter = {
			...filter,
			[key]: value,
		};
		setFilter(newFilter);
	};

	const handleSearch = clearSearch => {
		getAppsData(1, clearSearch);
		getPrfsData(1, clearSearch);
	};

	useEffect(() => {
		if (selectedData?.code) {
			setShowDesignSearch(true);
		} else {
			setShowDesignSearch(false);
		}
	}, [selectedData]);

	useEffect(() => {
		getAppsData(1, false);
		getPrfsData(1, false);
	}, []);

	return (
		<Modal
			title="Search"
			handleCancel={() => handleCloseModal()}
		>
			{!showDesignSearch ? (
				<>
					<Tabs
						action={newTab => setActiveTab(newTab)}
						activeTab={activeTab}
						classes="margin-bottom-16"
						data={tabData}
						mode="header"
					/>
					<div className="flex-row justify-flex-start align-items-flex-end">
						<div className="margin-right-16">
							<FormField
								label="App ID"
								type="text"
								action={e => { handleFilterChange('code', e.target.value); }}
								id="appId"
								name="appId"
								value={filter?.code || ''}
							/>
						</div>
						<div className="margin-right-16">
							<FormField
								label="Name"
								type="text"
								action={e => { handleFilterChange('name', e.target.value); }}
								id="name"
								name="name"
								value={filter?.name || ''}
							/>
						</div>
						<div className="margin-right-16">
							<FormField
								label="Short Name"
								type="text"
								action={e => { handleFilterChange('short', e.target.value); }}
								id="short"
								name="short"
								value={filter?.short || ''}
							/>
						</div>
						<div className="margin-right-16">
							<Button
								type="secondary"
								label="Clear"
								classes="margin-bottom-16"
								onClick={() => {
									setFilter({
										appId: '',
										name: '',
										short: '',
									});
									handleSearch(true);
								}}
								isInline
							/>
						</div>
						<div>
							<Button
								type="primary"
								label="Search"
								classes="margin-bottom-16"
								onClick={() => handleSearch(false)}
								isInline
							/>
						</div>
					</div>
					<table className="table is-bordered margin-bottom-32">
						<thead>
							<tr>
								<th>App ID</th>
								<th>Name</th>
								<th>Short Name</th>
								<th>Regions</th>
							</tr>
						</thead>
						<tbody>
							{activeTab === 0 ? (
								<>
									{appsData?.length ? (
										appsData.map((item, index) => (
											// eslint-disable-next-line react/no-array-index-key
											<tr className="is-clickable" key={index} role="presentation" onClick={() => setSelectedData(item)}>
												<td>{item.code}</td>
												<td>{item.name}</td>
												<td>{item.short}</td>
												<td>{item.regions?.length ? item.regions.join(', ') : '-'}</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan={4} className="grey-text text-center">No data available.</td>
										</tr>
									)}
								</>
							) : (
								<>
									{prfsData?.length ? (
										prfsData.map((item, index) => (
											// eslint-disable-next-line react/no-array-index-key
											<tr className="is-clickable" key={index} role="presentation" onClick={() => setSelectedData(item)}>
												<td>{item.code}</td>
												<td>{item.name}</td>
												<td>{item.short}</td>
												<td>{item.regions?.length ? item.regions.join(', ') : '-'}</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan={4} className="grey-text text-center">No data available.</td>
										</tr>
									)}
								</>
							)}
						</tbody>
					</table>
					{activeTab === 0 ? (
						<Pagination
							totalPages={appsPagination.totalPages}
							currentPage={appsPagination.currentPage}
							action={currentPage => handleAppsPagination(currentPage)}
						/>
					) : (
						<Pagination
							totalPages={prfsPagination.totalPages}
							currentPage={prfsPagination.currentPage}
							action={currentPage => handlePrfsPagination(currentPage)}
						/>
					)}
				</>
			) : (
				<>
					<div className="flex-row justify-space-between">
						<h4>Select an Application</h4>
						<ButtonIcon
							icon="chevron-left"
							label="Return to Previous Page"
							onClick={() => setSelectedData({})}
						/>
					</div>
					<DesignSearch
						type={type}
						handleApplyData={handleApplyData}
						selectedRefData={selectedData}
					/>
				</>
			)}
		</Modal>
	);
};

export default RefDataSearchModal;

RefDataSearchModal.propTypes = {
	type: PropTypes.string,
	handleCloseModal: PropTypes.func.isRequired,
	handleApplyData: PropTypes.func.isRequired,
};
